import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo, useState } from 'react';

import { MyReportsFilterBar } from './MyReportsFilterBar';
import { MyReportsListMeta } from './MyReportsListMeta';
import { ReportEditFragment } from './MyReportsListTable/ReportEditFragment';
import { MyReportsTable } from './MyReportsTable';
import { MyReport } from './MyReportsTable.types';
import { MyReportsListDataReturn } from './useMyReportsListData';
import { ActionPermissions, CustomPermission, ReportAccessData } from '../../../@types';
import { PopupDialog } from '../../../components/molecules/PopupDialog';
import { isUserHasPermission } from '../../../configs/permissions';
import { ActionableReport, useReportActions } from '../../../hooks/useReportActions';
import {
  useDeleteReportAccessDataById,
  useUpdateReportAccessDataById,
} from '../../../queries/report-query';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { toast } from '../../../utils/toast';

export interface MyReportsPanelProps {
  /** The list state, so the host owns whether the page is locked. */
  data: MyReportsListDataReturn;
  /**
   * When set, clicking a report name calls this instead of navigating to its
   * list page — used when the panel is embedded in a page's Reports modal.
   */
  onSelectReport?: (row: MyReport) => void;
}

/**
 * The My Reports list itself: filters, result count, table, and the edit and
 * remove flows. Deliberately free of page chrome (no header, no PageContainer,
 * no document title) so it can serve both the My Reports page and the Reports
 * modal on the Jobs and Parts pages.
 */
export const MyReportsPanel: React.FC<MyReportsPanelProps> = ({ data, onSelectReport }) => {
  const theme = useTheme();
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const permissions: CustomPermission[] | undefined = getUserPermissionsQuery.data;

  const { isPinned, togglePin, toggleDefault } = useReportActions();
  const deleteReportQuery = useDeleteReportAccessDataById();
  const updateAccessQuery = useUpdateReportAccessDataById();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeRow, setActiveRow] = useState<MyReport | undefined>();

  const hasSearchPermission = useMemo(
    () => isUserHasPermission(ActionPermissions.Report_My_Report_Search, permissions),
    [permissions]
  );

  // Pinning and defaults are both scoped to the report's own page, not the page
  // the list happens to be shown on — so they behave identically here and in a
  // list page's Reports modal.
  const asActionable = useCallback(
    (row: MyReport): ActionableReport => ({
      id: row.id,
      code: row.code,
      name: row.name,
      isDefault: row.isDefault,
      pageName: row.savedReportType?.name ?? row.pageName ?? '',
    }),
    []
  );

  const allRows = useMemo(
    () => [...data.myReports, ...data.sharedReports, ...data.otherReports],
    [data.myReports, data.sharedReports, data.otherReports]
  );

  const handleTogglePin = useCallback(
    (row: MyReport) => togglePin(asActionable(row)),
    [togglePin, asActionable]
  );

  const handleToggleDefault = useCallback(
    async (row: MyReport) => {
      try {
        await toggleDefault(asActionable(row), allRows.map(asActionable));
        toast.success(
          row.isDefault ? `"${row.name}" removed as default` : `"${row.name}" set as default`
        );
      } catch {
        toast.error('Failed to update the default report. Please try again.');
      } finally {
        data.refetch();
      }
    },
    [toggleDefault, asActionable, allRows, data]
  );

  const handleEdit = useCallback((row: MyReport) => {
    setActiveRow(row);
    setEditOpen(true);
  }, []);

  const handleDeleteRequest = useCallback((row: MyReport) => {
    setActiveRow(row);
    setConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setConfirmOpen(false);
    if (!activeRow) return;

    let didRemove = false;
    try {
      const mySiblingId = data.mySiblingId;
      if (mySiblingId != null && activeRow.reportOwner?.id === mySiblingId) {
        await deleteReportQuery.mutateAsync(activeRow.id);
        didRemove = true;
      } else {
        const access = activeRow.savedReportAccess?.find(
          (a: ReportAccessData) => a.entityId === mySiblingId
        );
        if (access?.id != null && access.entityId != null) {
          await updateAccessQuery.mutateAsync({
            id: access.id,
            data: {
              entityId: access.entityId,
              canEdit: access.canEdit ?? false,
              canCustomizeColumns: access.canCustomizeColumns ?? false,
              canCustomizeSearch: access.canCustomizeSearch ?? false,
              isActive: false,
            },
          });
          didRemove = true;
        }
      }
      if (didRemove) {
        toast.success('Report removed');
      } else {
        toast.warning('Could not remove the report — no matching access was found.');
      }
    } catch {
      toast.error('Failed to remove the report. Please try again.');
    } finally {
      if (didRemove) data.refetch();
    }
  }, [activeRow, data, deleteReportQuery, updateAccessQuery]);

  if (!hasSearchPermission) {
    // `permissions` is undefined until the query settles, so this branch is also
    // the first-paint state for a user who *does* have the permission. Saying so
    // out loud would flash a denial at them before their permissions arrive.
    if (getUserPermissionsQuery.isPending) return null;
    return (
      <Typography sx={{ mt: 4, px: '20px', color: theme.palette.text.secondary }}>
        You do not have permission to view reports.
      </Typography>
    );
  }

  return (
    <>
      <MyReportsFilterBar
        pageToggleOptions={data.pageToggleOptions}
        scopeToggleOptions={data.scopeToggleOptions}
        ownerOptions={data.ownerOptions}
        filters={data.filters}
        onChange={data.setFilters}
        onReset={data.resetFilters}
        isPageLocked={data.isPageLocked}
      />
      <MyReportsListMeta
        shown={data.total}
        total={data.totalVisible}
        hasActiveFilters={data.hasActiveFilters}
      />
      <MyReportsTable
        myReports={data.myReports}
        sharedReports={data.sharedReports}
        otherReports={data.otherReports}
        mySiblingId={data.mySiblingId}
        sortModel={data.sortModel}
        onSortChange={data.setSortModel}
        onEdit={handleEdit}
        onDelete={handleDeleteRequest}
        loading={data.isFetching || data.isInitialLoad}
        isError={data.isError}
        hasActiveFilters={data.hasActiveFilters}
        onSelectReport={onSelectReport}
        isPinned={(row) => isPinned(asActionable(row))}
        onTogglePin={handleTogglePin}
        onToggleDefault={handleToggleDefault}
      />

      <ReportEditFragment
        open={editOpen}
        closeModel={() => setEditOpen(false)}
        rowData={activeRow}
        refetchReports={(refetch) => {
          if (refetch) data.refetch();
        }}
      />
      <PopupDialog
        maxWidth="450px"
        title="Confirm the action"
        isOpen={confirmOpen}
        actionLabel="Yes"
        onClose={(_event?: unknown, reason?: string) =>
          reason !== 'backdropClick' && setConfirmOpen(false)
        }
        onSave={handleConfirmDelete}
      >
        Are you sure you want to remove this report?
      </PopupDialog>
    </>
  );
};
